import { useEffect, useMemo } from "react";

import { useNavigate } from "react-router-dom";

import { useAssetsHoldingsData } from "domains/altCustody/components/AssetHoldingsTab/useAssetsHoldingsData";
import { liquidityApiReducer } from "domains/liquidity/store/liquidity.api.reducer";
import { TAssetType } from "domains/liquidity/shared/types";
import { URLs } from "common/lib/constants";
import { useTrustbenSelector } from "domains/trustben";

export const useFullInvestments = () => {
  const navigate = useNavigate();
  const { investments, loading: isInvestmentsLoading } = useAssetsHoldingsData();
  const { isTrustbenAltQuote } = useTrustbenSelector();
  
  const shouldSkipAllAssets = isTrustbenAltQuote;
  
  const {
    data: assets,
    isFetching: isAssetsFetching,
    refetch: refetchAssets,
  } = liquidityApiReducer.useFetchAllAssetsQuery(undefined, {
    skip: shouldSkipAllAssets,
  });

  useEffect(() => {
    if (!isInvestmentsLoading && !shouldSkipAllAssets) {
      refetchAssets();
    }
  }, [ isInvestmentsLoading, refetchAssets, shouldSkipAllAssets ]);

  const fullInvestments = useMemo(() => {
    if (!investments?.length && !assets?.length) {
      return [];
    }
    const allInvestments = investments?.map((investment) => {
      const asset = assets?.find(({ asset }) => asset?.fund_id === investment?.fund_id);
      return { ...investment, sponsor: asset?.asset?.sponsor ?? "" };
    });
    return allInvestments;
  }, [ investments, assets ]);

  const getAssetsWithFundManager = (prevAssets: any[]): any[] => {
    return prevAssets?.map((prevAsset) => {
      const asset = assets?.find(({ asset }) => asset.fund_id === prevAsset.fund_id);
      return { ...prevAsset, sponsor: asset?.asset?.sponsor ?? '' };
    });
  };

  const isInvestmentInfoCompleted = (asset: TAssetType) => [
    'capital_commitment',
    'current_net_asset_value',
    'unfunded_commitment_currency',
  ].every(field => asset?.[ field as keyof TAssetType ] != null);

  const navigateToEditAsset = (assetId: string) => {
    navigate(`${ URLs.PROTECTED.MY_ALT_CUSTODY_ASSETS }/${ assetId }/edit?from=asset-selection`);
  };

  const setInvestmentInfoCompleted = (assets: TAssetType[]) => 
    assets.map((asset) => ({ 
      ...asset, 
      is_investment_info_complete: true, 
    }));

  const isLoadingData = isInvestmentsLoading || isAssetsFetching;

  return {
    investments: isLoadingData ? [] : fullInvestments?.slice(0),
    getAssetsWithFundManager,
    isInvestmentInfoCompleted,
    navigateToEditAsset,
    setInvestmentInfoCompleted,
    loading: isLoadingData,
  };
};
